import { useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http, errMsg, rp, CONTACT_PRICE, formatDate, packageImageUrl } from "@/lib/api";
import { F, inputCls, StatusPill, ConfirmDelete, MoveButtons, moveItem, PublishControls, FormButtons, saveItem, linesToList, ListToolbar, matches } from "@/components/admin/shared";

const blank = { name: "", description: "", category_ids: [], regular_price: "", promo_enabled: false, promo_price: "", discount_label: "", promo_start: "", promo_end: "", terms: "", featured: false, hide_text: false };

const ImageField = ({ f, setF, onSaved }) => {
  const [busy, setBusy] = useState(false);
  if (!f.id) return <p className="sm:col-span-2 text-[11px] text-nanina-muted">Simpan draft dulu untuk mengunggah foto/poster promo.</p>;
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await http.post(`/admin/packages/${f.id}/image`, fd);
      setF({ ...f, image_path: r.data.image_path, updated_at: r.data.updated_at });
      toast.success("Gambar promo diunggah");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try {
      const r = await http.delete(`/admin/packages/${f.id}/image`);
      setF({ ...f, image_path: null, updated_at: r.data.updated_at });
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="sm:col-span-2 space-y-2">
      {f.image_path && (
        <div className="flex items-center gap-3">
          <img src={packageImageUrl({ ...f, published_at: null })} alt="Poster promo" className="h-24 w-20 rounded-xl object-contain bg-nanina-bg border border-nanina-line" data-testid="package-image-preview" />
          <button type="button" onClick={remove} className="text-xs text-red-600" data-testid="package-image-remove">Hapus gambar</button>
        </div>
      )}
      <F label="Foto / poster promo (JPG/PNG/WEBP maks 8 MB)"><input type="file" accept="image/jpeg,image/png,image/webp" className={inputCls} onChange={upload} disabled={busy} data-testid="package-image-input" /></F>
      <label className="flex items-center gap-2 text-sm text-nanina-green"><input type="checkbox" checked={!!f.hide_text} onChange={(e) => setF({ ...f, hide_text: e.target.checked })} data-testid="package-hide-text-checkbox" /> Poster sudah memuat teks (sembunyikan deskripsi & syarat di kartu)</label>
    </div>
  );
};

export const promoState = (p) => {
  if (!p.promo_enabled) return "Promo nonaktif";
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  if (today < p.promo_start) return "Promo terjadwal";
  if (today > p.promo_end) return "Promo berakhir";
  return "Promo aktif";
};

const PackageForm = ({ initial, categories, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleCat = (id) => setF({ ...f, category_ids: f.category_ids.includes(id) ? f.category_ids.filter((x) => x !== id) : [...f.category_ids, id] });
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = async (e, publish = false) => {
    e?.preventDefault();
    setBusy(true);
    const body = { name: f.name, description: f.description, category_ids: f.category_ids, promo_enabled: f.promo_enabled, discount_label: f.discount_label || "", featured: !!f.featured, hide_text: !!f.hide_text, regular_price: num(f.regular_price), promo_price: num(f.promo_price), promo_start: f.promo_start || null, promo_end: f.promo_end || null, terms: linesToList(f.terms) };
    try {
      const isNew = !f.id;
      await saveItem("packages", f, body, publish, (d) => setF((x) => ({ ...x, id: d.id })));
      toast.success(publish ? "Paket disimpan & diterbitkan" : isNew ? "Draft disimpan. Anda bisa mengunggah foto/poster sekarang." : "Draft paket disimpan");
      onSaved();
      if (publish || !isNew) onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-3" data-testid="package-form">
      <F label="Judul paket / promo *" className="sm:col-span-2"><input required maxLength={120} className={inputCls} value={f.name} onChange={set("name")} data-testid="package-name-input" /></F>
      <ImageField f={f} setF={setF} onSaved={onSaved} />
      <F label="Deskripsi singkat" className="sm:col-span-2"><textarea rows={2} maxLength={600} className={inputCls} value={f.description} onChange={set("description")} data-testid="package-description-input" /></F>
      <div className="sm:col-span-2">
        <p className="text-xs font-semibold text-nanina-green">Layanan yang termasuk</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button type="button" key={c.id} onClick={() => toggleCat(c.id)} className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${f.category_ids.includes(c.id) ? "bg-nanina-green text-white border-nanina-green" : "bg-white text-nanina-green border-nanina-line"}`} data-testid={`package-category-${c.slug}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <F label="Harga normal (Rp)" hint="Kosongkan bila belum ada harga resmi"><input type="number" min="0" className={inputCls} value={f.regular_price ?? ""} onChange={set("regular_price")} data-testid="package-regular-price-input" /></F>
      <label className="flex items-center gap-2 text-sm text-nanina-green self-end pb-2"><input type="checkbox" checked={f.promo_enabled} onChange={(e) => setF({ ...f, promo_enabled: e.target.checked })} data-testid="package-promo-enabled-checkbox" /> Aktifkan promo</label>
      {f.promo_enabled && (
        <>
          <F label="Harga promo (Rp)"><input type="number" min="0" className={inputCls} value={f.promo_price ?? ""} onChange={set("promo_price")} data-testid="package-promo-price-input" /></F>
          <F label="Label diskon" hint="Contoh: Hemat 10%"><input maxLength={60} className={inputCls} value={f.discount_label} onChange={set("discount_label")} data-testid="package-discount-label-input" /></F>
          <F label="Mulai promo *"><input type="date" required className={inputCls} value={f.promo_start || ""} onChange={set("promo_start")} data-testid="package-promo-start-input" /></F>
          <F label="Berakhir promo *"><input type="date" required className={inputCls} value={f.promo_end || ""} onChange={set("promo_end")} data-testid="package-promo-end-input" /></F>
        </>
      )}
      <F label="Syarat & ketentuan" hint="Satu poin per baris" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={f.terms} onChange={set("terms")} data-testid="package-terms-input" /></F>
      <label className="sm:col-span-2 flex items-center gap-2 text-sm text-nanina-green"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="package-featured-checkbox" /> Paket unggulan</label>
      <FormButtons busy={busy} onPublish={() => save(null, true)} testId="package" />
    </form>
  );
};

export const PackagesPanel = ({ categories, packages, reload }) => {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const list = packages.map((p, i) => ({ p, i })).filter(({ p }) => matches(p, q, status, ["name", "description"]));
  const remove = async (p) => { try { await http.delete(`/admin/packages/${p.id}`); toast.success("Paket dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const move = async (i, d) => { if (await moveItem(packages, i, d, "/admin/packages/reorder")) reload(); };
  const open = (p) => setEditing(p ? { ...p, terms: (p.terms || []).join("\n") } : { ...blank });
  return (
    <div data-testid="packages-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-nanina-muted">Paket dan promo tidak mengubah harga layanan asli. Promo yang belum mulai atau sudah berakhir otomatis tidak tampil di website. Gunakan Pratinjau sebelum menerbitkan.</p>
        <button onClick={() => open(null)} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-nanina-green text-white text-sm font-semibold px-4 py-2" data-testid="package-add-button"><Plus className="h-4 w-4" /> Tambah paket</button>
      </div>
      {packages.length === 0 && <p className="rounded-2xl border border-dashed border-nanina-line p-6 text-center text-sm text-nanina-muted">Belum ada paket. Bagian "Paket & Promo" tersembunyi di website.</p>}
      <ListToolbar q={q} setQ={setQ} status={status} setStatus={setStatus} testId="package" />
      <ul className="space-y-2">
        {list.map(({ p, i }) => (
          <li key={p.id} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white border border-nanina-line p-3" data-testid={`admin-package-${p.id}`}>
            {p.image_path ? <img src={packageImageUrl({ ...p, published_at: null })} alt="" className="h-14 w-12 rounded-lg object-cover bg-nanina-bg" /> : <span className="h-14 w-12 rounded-lg bg-nanina-bg grid place-items-center text-[9px] text-nanina-muted">Tanpa foto</span>}
            <div className="min-w-0 flex-1 basis-[calc(100%-4rem)] sm:basis-0">
              <p className="font-semibold text-sm text-nanina-green">{p.name}</p>
              <p className="text-xs text-nanina-muted">
                {p.regular_price ? rp(p.regular_price) : CONTACT_PRICE}
                {p.promo_enabled && ` · Promo ${p.promo_price != null ? rp(p.promo_price) : p.discount_label} (${formatDate(p.promo_start)} – ${formatDate(p.promo_end)})`}
              </p>
              <span className="inline-flex flex-wrap gap-1"><StatusPill item={p} /><span className="text-[10px] rounded-full bg-nanina-skysoft px-2 py-0.5 text-nanina-green" data-testid={`package-promo-state-${p.id}`}>{promoState(p)}</span></span>
            </div>
            <PublishControls item={p} coll="packages" reload={reload} testId={`package-${p.id}`} />
            {!q && !status && <MoveButtons onUp={() => move(i, -1)} onDown={() => move(i, 1)} testId={`package-move-${p.id}`} />}
            <button onClick={() => open(p)} className="p-2 rounded-lg hover:bg-nanina-bg" aria-label="Edit" data-testid={`package-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <ConfirmDelete label={p.name} onConfirm={() => remove(p)} testId={`package-delete-${p.id}`} />
          </li>
        ))}
      </ul>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit paket" : "Tambah paket"}</DialogTitle><DialogDescription>Gunakan hanya harga dan promo yang sudah disetujui.</DialogDescription></DialogHeader>
          {editing && <PackageForm initial={editing} categories={categories} onSaved={reload} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
